import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { TrialShell, TrialAlert, TrialField } from '../components/trial/TrialShell';
import { TrialProgress } from '../components/trial/TrialProgress';
import { trialApi, readTrialToken, saveTrialToken, trialError } from '../components/trial/trialApi';

export default function TrialOnboardingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { api, setToken: setAuthToken, setUser } = useAuth();
  const once = useRef(false);
  const [token, setSignupToken] = useState(() => new URLSearchParams(location.hash.slice(1)).get('trial_token') || readTrialToken());
  const [legacy] = useState(() => ({ token: new URLSearchParams(location.search).get('token'), email: new URLSearchParams(location.search).get('email') }));
  const [sessionId] = useState(() => new URLSearchParams(location.search).get('session_id'));
  const [state, setState] = useState(null);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [visible, setVisible] = useState(false);
  const adopt = data => {
    localStorage.setItem('token', data.token); setAuthToken(data.token); setUser(data.user);
    localStorage.setItem('currentWorkspaceId', data.user.workspace_id);
    setReady(true);
  };
  const load = async () => {
    setLoading(true); setError('');
    try {
      let currentToken = token;
      if (legacy.token && legacy.email && !currentToken) {
        const result = await trialApi.post('/legacy-link', legacy);
        currentToken = result.data.signup_token; setSignupToken(currentToken);
      }
      if (!currentToken) { setError('Open the secure link in your welcome email to finish setup.'); return; }
      saveTrialToken(currentToken);
      const { data } = await trialApi.post('/onboarding', { signup_token: currentToken, session_id: sessionId });
      setState(data);
      if (data.google_verified) {
        const result = await trialApi.post('/complete', { signup_token: currentToken, session_id: sessionId });
        adopt(result.data);
      } else if (!data.needs_password) {
        try {
          const me = await api.get('/api/auth/session');
          if (me.data.user?.email.toLowerCase() === data.email.toLowerCase()) { setUser(me.data.user); setReady(true); }
        } catch (_) { /* A completed account must sign in, never reuse the setup token. */ }
      }
    } catch (err) { setError(trialError(err)); } finally { setLoading(false); }
  };
  useEffect(() => {
    if (once.current) return;
    once.current = true;
    if (location.hash || legacy.token) navigate({ pathname: '/trial-onboarding', search: sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : '' }, { replace: true });
    load();
  }, []);
  const submit = async e => {
    e.preventDefault(); setError('');
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setBusy(true);
    try { const result = await trialApi.post('/complete', { signup_token: token, session_id: sessionId, password }); adopt(result.data); }
    catch (err) { setError(trialError(err)); } finally { setBusy(false); }
  };
  return <TrialShell ready={ready} focused>
    <div data-testid="trial-onboarding-page">
      {!ready && <TrialProgress current={3} />}
      <h1 className="trial-heading" data-testid="trial-onboarding-title">{ready ? `You're all set${state?.first_name ? `, ${state.first_name}` : ''}.` : loading ? 'Almost there' : state?.needs_password ? 'Set your password' : 'Welcome to Voxinity AI'}</h1>
      {loading ? <div className="trial-loading" data-testid="trial-checkout-verifying"><Loader2 size={20} className="animate-spin" /> Confirming your account…</div> : <>
        <TrialAlert>{error}</TrialAlert>
        {ready ? <>
          <p className="trial-description" data-testid="trial-workspace-ready">Your workspace is ready.</p>
          <button type="button" className="trial-primary" onClick={() => navigate(`/workspace/${state.workspace_id}`, { replace: true })} data-testid="enter-workspace-button">Get started <ArrowRight size={17} /></button>
          <div className="trial-community-quiet" data-testid="trial-community-invitation"><a href="https://discord.gg/cUgxTYnpM" target="_blank" rel="noopener noreferrer" data-testid="join-community-button">Join the private Voxinity AI community on Discord <ArrowUpRight size={14} /></a></div>
        </> : state?.needs_password ? <>
          <p className="trial-email-line" data-testid="trial-setup-email">{state.email}</p>
          <form onSubmit={submit} data-testid="trial-set-password-form"><div className="trial-password-wrap"><TrialField label="Password" id="trial-password-input" type={visible ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" minLength={8} maxLength={128} placeholder="At least 8 characters" required disabled={busy} /><button type="button" className="trial-password-toggle" aria-label={visible ? 'Hide password' : 'Show password'} onClick={() => setVisible(!visible)} data-testid="trial-password-visibility">{visible ? <EyeOff size={18} /> : <Eye size={18} />}</button></div><TrialField label="Confirm password" id="trial-password-confirm" type={visible ? 'text' : 'password'} value={confirm} onChange={e => setConfirm(e.target.value)} autoComplete="new-password" minLength={8} maxLength={128} placeholder="Re-enter password" required disabled={busy} /><button type="submit" className="trial-primary" disabled={busy} data-testid="trial-set-password-submit">{busy ? <Loader2 size={17} className="animate-spin" /> : <>Continue <ArrowRight size={16} /></>}</button></form>
        </> : state ? <><p className="trial-description" data-testid="trial-password-already-set">Your password is already set. Log in to get started.</p><Link to="/login" className="trial-primary" data-testid="trial-onboarding-login">Log in <ArrowRight size={16} /></Link></> : <>
          {token && <button className="trial-secondary" onClick={load} data-testid="trial-verification-retry">Check again</button>}
          <p className="trial-login"><Link to="/free-trial" data-testid="trial-resume-signup-link">Continue signup or request a new link</Link></p>
          <p className="trial-login"><Link to="/login" data-testid="trial-recovery-login-link">Already finished? Log in</Link></p>
        </>}
      </>}
    </div>
  </TrialShell>;
}